import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { curatedFeedService } from '@/services/curatedFeedService'
import type { CuratedFeedItem, CuratedFeedSource } from '@/services/curatedFeedService'
import { feedTopicPreferencesService } from '@/services/feedTopicPreferencesService'
import { CuratedFeedTopic } from '@/services/curatedSourceService'
import { ResponsiveCardGrid } from '@/components/layout/ResponsiveCardGrid'
import CuratedItemCard from './CuratedItemCard'
import TopicSelector from './TopicSelector'
import SourceFilter from './SourceFilter'

const PAGE_SIZE = 20

const CuratedFeedList: React.FC = () => {
  const [items, setItems] = useState<CuratedFeedItem[]>([])
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [subscribedTopics, setSubscribedTopics] = useState<CuratedFeedTopic[]>([])
  const [savingTopics, setSavingTopics] = useState(false)
  const [sources, setSources] = useState<CuratedFeedSource[]>([])
  const [sourceId, setSourceId] = useState<string | undefined>(undefined)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const loadPage = useCallback(async (nextCursor?: string, sourceFilter?: string): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const page = await curatedFeedService.getFeed({
        limit: PAGE_SIZE,
        ...(nextCursor && { cursor: nextCursor }),
        ...(sourceFilter && { sourceId: sourceFilter })
      })
      setItems((prev) => {
        if (!nextCursor) return page.items
        const seen = new Set(prev.map((i) => i.id))
        return [...prev, ...page.items.filter((i) => !seen.has(i.id))]
      })
      setHasMore(page.hasMore)
      setCursor(page.nextCursor)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load curated feed')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void feedTopicPreferencesService.getPreferences().then((prefs) => {
      setSubscribedTopics(prefs.subscribedTopics)
    })
  }, [])

  useEffect(() => {
    void curatedFeedService.listSources().then(setSources)
  }, [])

  useEffect(() => {
    void loadPage(undefined, sourceId)
  }, [loadPage, sourceId])

  useEffect(() => {
    if (!hasMore || !sentinelRef.current) return
    const node = sentinelRef.current
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0]
      if (entry?.isIntersecting && !loading) {
        void loadPage(cursor, sourceId)
      }
    }, { rootMargin: '300px' })
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, cursor, loadPage, loading, sourceId])

  const handleTopicsChange = async (topics: CuratedFeedTopic[]): Promise<void> => {
    setSubscribedTopics(topics)
    setSavingTopics(true)
    try {
      await feedTopicPreferencesService.updatePreferences(topics)
      await loadPage(undefined, sourceId)
    } finally {
      setSavingTopics(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <TopicSelector value={subscribedTopics} onChange={handleTopicsChange} disabled={savingTopics} />
        <SourceFilter sources={sources} value={sourceId} onChange={setSourceId} disabled={loading} />
      </div>
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      {items.length > 0 && (
        <ResponsiveCardGrid>
          {items.map((item) => (
            <CuratedItemCard key={item.id} item={item} />
          ))}
        </ResponsiveCardGrid>
      )}
      {!loading && items.length === 0 && !error && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
          No curated articles yet. Check back soon.
        </div>
      )}
      {hasMore && <div ref={sentinelRef} aria-hidden="true" className="h-1" />}
      {loading && (
        <div className="flex items-center justify-center py-4 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}
    </div>
  )
}

export default CuratedFeedList
